// @ts-strict-ignore
import * as React from 'react';
import { SetupViewProps, SetupStep, AppComponentProps, SetupStepType } from '../../../typescriptHelpers';
import { TagOutlined, ArrowRightOutlined, CheckOutlined, LinkOutlined } from '@ant-design/icons';
import {
    IntegrationCapability,
    PatternToken,
    ReleaseTag,
    ReleaseTagPattern,
    DisallowedTokensForTag,
    RequireAtLeastOneOfTokensForTag,
    SelectableCommit,
    AllPatternTokens,
} from '../../../types';
import {
    selectAppSettings,
    selectAuthCredentials,
    selectVCSIntegration,
    selectWasAppCreatedAfterQualitativeOnboarding,
    selectHasVCSIntegrationConnected,
    selectLastCompletedVersionTag,
    selectLatestVersion,
    selectHasAtLeastNReleasesTagged,
    selectUserIsAdmin,
} from '../../../selectors/selectors';
import { antGrey, colorWithAlpha, runwayColors, rwColors } from '../../styles/globalStyles';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Collapse from 'antd/lib/collapse';
import Spin from 'antd/lib/spin';
import Alert from 'antd/lib/alert';
import Tooltip from 'antd/lib/tooltip';
import { debounce, isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import { useForm } from 'antd/lib/form/Form';
import { AutomationTip, InfoTip, HelpTip } from '../shared';
import { validatePattern } from '../../../helpers';
import { AppSettingsUpdatable } from '../../../actions/updatable';
import { tryUpdateAppSettings } from '../../../actions/appSettings';
import * as API from '../../../api';
import { iconAndAltForIntegrationType } from '../../../helpers/integrationHelpers';
import { getSetupStepLocalData } from '../../../helpers/localStorageHelper';
import { globalMessage } from '../../../utils/globalMessage';
import { useState } from 'react';
import { AppEntityRequest } from '../../../actions/entities';
import { InfoCircleOutlined } from '@ant-design/icons';
import { CommitSelector } from '../../CommitSelectorComponent';
import FormItem from 'antd/lib/form/FormItem';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import SupportedTokensComponent from '@/components/common/SupportedTokensComponent';

interface OptionalReleaseTagModuleProps extends SetupViewProps {
    releaseTag: ReleaseTagPattern;
    taggedCommit: ReleaseTag;
}
const OptionalReleaseTagModule: React.FC<OptionalReleaseTagModuleProps> = ({
    appId,
    orgId,
    releaseTag,
    taggedCommit,
    displayRelatedIntegrationsSetup,
}) => {
    const [form] = useForm();

    const latestCompletedVersion = useSelector(
        (state: RootState) => selectAppSettings(state, appId).latestCompletedVersion,
    );

    const [version, setVersion] = React.useState(latestCompletedVersion);

    const [isLoadingCommits, setIsLoadingCommits] = React.useState(true);
    const [commits, setCommits] = React.useState<SelectableCommit[]>([]);

    const dispatch = useDispatch();

    const tagCommitIsLoading = useIsLoading(AppSettingsUpdatable.TagCommit);
    const tagCommitError = useError(AppSettingsUpdatable.TagCommit);
    const tagCommitSuccess = useUpdated(AppSettingsUpdatable.TagCommit);

    React.useEffect(() => {
        if (tagCommitError) {
            globalMessage.error(tagCommitError);
        }
    }, [tagCommitError]);

    React.useEffect(() => {
        if (tagCommitSuccess) {
            globalMessage.success('Successfully tagged commit!');
        }
    }, [tagCommitSuccess]);

    const tagCommit = ({ commitHash, tagName, version }) => {
        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.TagCommit, {
                orgId,
                appId,
                commitHash,
                tagName,
                version,
            }),
        );
    };

    const initialValues = {
        version: latestCompletedVersion,
        commitHash: null,
        tagName: null,
    };

    const vcsIntegration = useSelector((state: RootState) => selectVCSIntegration(state, appId));

    const [integrationIcon, integrationIconAlt] = vcsIntegration ? iconAndAltForIntegrationType(vcsIntegration.id) : [];

    const debouncedResults = React.useMemo(() => {
        return debounce((version) => {
            if (version) {
                setVersion(version);
            }
        }, 500);
    }, []);

    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));
    React.useEffect(() => {
        if (!taggedCommit && vcsIntegration && authCredentials && form.getFieldError('version').length === 0) {
            setIsLoadingCommits(true);
            API.getCandidateSelectableCommits(
                {
                    appId,
                    orgId,
                    version,
                },
                authCredentials,
            )
                .then(({ commits }) => {
                    setCommits(commits);
                })
                .catch((error) => {
                    globalMessage.error(error.message);
                })
                .finally(() => {
                    setIsLoadingCommits(false);
                });
        }

        // cancel the api call if the version changes
        return () => {
            setIsLoadingCommits(false);
            debouncedResults.cancel();
        };
    }, [version, taggedCommit]);

    // require manual tag name if pattern calls for anything other than {version}/{versionConcise}
    let needsManualTagName = false;
    for (const token of [
        PatternToken.ReleaseName,
        PatternToken.BuildAppStore,
        PatternToken.BuildCI,
        PatternToken.CIBuildsCount,
    ]) {
        if (releaseTag?.pattern.includes(token)) {
            needsManualTagName = true;
            break;
        }
    }

    const formattedVersionTag = releaseTag?.pattern.split('{version}').join(version);
    const vcsCommand = `git tag -a ${
        latestCompletedVersion ? formattedVersionTag
        : needsManualTagName && releaseTag ? releaseTag?.pattern
        : 'X.X.X'
    } <hash> -m "Message here"`;

    if (taggedCommit) {
        return null;
    }

    if (!vcsIntegration) {
        return (
            <Space direction="vertical" style={{ width: '100%' }} size={20}>
                <Typography.Text style={{ color: antGrey.grey8, fontSize: 14, fontWeight: 400 }}>
                    Use this helper git command to tag your last release:
                </Typography.Text>
                <Space direction="horizontal" size={8}>
                    <Text code style={{ fontSize: 16, color: antGrey.grey7 }}>
                        {vcsCommand}
                    </Text>
                    <Button
                        type="link"
                        onClick={() => {
                            navigator.clipboard.writeText(vcsCommand);
                            globalMessage.success('Copied to clipboard!');
                        }}
                    >
                        Copy to clipboard
                    </Button>
                </Space>

                <div
                    style={{
                        backgroundColor: colorWithAlpha(rwColors.primary, 0.12),
                        borderRadius: 8,
                        border: `1px solid ${colorWithAlpha(rwColors.primary, 0.35)}`,
                        padding: '14px 18px',
                        paddingBottom: 0,
                    }}
                >
                    <InfoTip>
                        Getting started tagging your releases (starting with your most recent) lets Runway accurately
                        reflect the work going into each release.
                        <br />
                        <br />
                        If you connect a version control system like GitHub or GitLab now, we can help you get started
                        by providing a list of commits and tagging your last release on your behalf.{' '}
                        <Button type="link" style={{ padding: 0 }} onClick={displayRelatedIntegrationsSetup}>
                            Connect a VCS
                        </Button>
                        <br />
                        <br />
                        Already tag your releases? Great! Connect a VCS and Runway will pull recent tags and reconcile
                        them with the correct releases in Runway.
                    </InfoTip>
                </div>
            </Space>
        );
    }

    return (
        <Form form={form} onFinish={tagCommit} initialValues={initialValues}>
            <Space direction="vertical" style={{ width: '100%' }} size={20}>
                <div>
                    <Typography.Text style={{ color: antGrey.grey7, fontSize: 14, fontWeight: 400 }}>
                        Runway needs at least your last release to be tagged,
                        <br /> so we can accurately reflect work going into your next upcoming release.
                    </Typography.Text>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'end', marginTop: 3 }}>
                        <img
                            src={integrationIcon}
                            alt={integrationIconAlt}
                            width={25}
                            height={25}
                            style={{ marginBottom: 3 }}
                        />
                        <FormItem
                            noStyle
                            name="commitHash"
                            rules={[{ required: true, message: 'Please select a commit to tag' }]}
                        >
                            <CommitSelector
                                loading={isLoadingCommits}
                                disabled={isLoadingCommits}
                                commits={commits}
                                style={{ flex: 1, maxWidth: 500, marginBottom: 0, marginTop: 8 }}
                            />
                        </FormItem>
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'end',
                        justifyContent: 'space-between',
                    }}
                >
                    <Space direction="vertical" size={20}>
                        <Form.Item
                            name="version"
                            label="Last release version:"
                            style={{
                                flex: 1,
                                marginBottom: 0,
                                color: antGrey.grey8,
                                fontSize: 14,
                                fontWeight: 400,
                            }}
                            rules={[
                                { required: true, message: 'Please input a version' },
                                { pattern: /^\d+(?:\.\d+)?(?:\.\d+)?$/, message: 'Invalid version' },
                            ]}
                        >
                            <Input
                                style={{ maxWidth: 120 }}
                                placeholder="X.X.X"
                                autoComplete="off"
                                onChange={(e) => {
                                    form.resetFields(['taggableCommit']);
                                    debouncedResults(e.target.value);
                                }}
                            />
                        </Form.Item>
                        {needsManualTagName && (
                            <Form.Item
                                name="tagName"
                                label="Tag name:"
                                style={{
                                    flex: 1,
                                    marginBottom: 0,
                                    color: antGrey.grey8,
                                    fontSize: 14,
                                    fontWeight: 400,
                                }}
                                rules={[{ required: true, message: 'Please input a tag name' }]}
                            >
                                <Input style={{ maxWidth: 240 }} placeholder={releaseTag?.pattern} autoComplete="off" />
                            </Form.Item>
                        )}
                    </Space>

                    <Form.Item style={{ marginBottom: 0 }} shouldUpdate>
                        {() => (
                            <Button
                                onClick={() => {
                                    form.validateFields();
                                }}
                                loading={tagCommitIsLoading}
                                disabled={
                                    isLoadingCommits ||
                                    form.getFieldError('version').length > 0 ||
                                    form.getFieldError('tagName').length > 0
                                }
                                type="primary"
                                htmlType="submit"
                                icon={<TagOutlined />}
                            >
                                Tag release{' '}
                                {!form.getFieldValue('version') || form.getFieldError('version').length > 0 ?
                                    'now'
                                :   `${form.getFieldValue('version')} now`}
                            </Button>
                        )}
                    </Form.Item>
                </div>
            </Space>
        </Form>
    );
};

interface ReleaseTagPatternModuleProps extends SetupViewProps {
    releaseTag: ReleaseTagPattern;
}
const ReleaseTagPatternModule: React.FC<ReleaseTagPatternModuleProps> = ({ appId, orgId, onChange, releaseTag }) => {
    const [form] = useForm();

    const dispatch = useDispatch();

    const addReleaseTagPatternIsLoading = useIsLoading(AppSettingsUpdatable.NewReleaseTagPattern);
    const addReleaseTagPatternError = useError(AppSettingsUpdatable.NewReleaseTagPattern);
    const addReleaseTagPatternSuccess = useUpdated(AppSettingsUpdatable.NewReleaseTagPattern);

    const [releaseTagGuess, setReleaseTagGuess] = useState('');
    const [releaseTagGuessIsLoading, setReleaseTagGuessIsLoading] = useState(false);

    React.useEffect(() => {
        if (addReleaseTagPatternSuccess) {
            globalMessage.success('Release tag pattern added!');
            onChange(false);
        }
    }, [addReleaseTagPatternSuccess]);

    const addReleaseTagPattern = (values) => {
        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.NewReleaseTagPattern, {
                orgId,
                appId,
                pattern: values.releaseTagPattern,
            }),
        );
    };

    const editReleaseTagPatternIsLoading = useIsLoading(AppSettingsUpdatable.UpdateReleaseTagPattern);
    const editReleaseTagPatternError = useError(AppSettingsUpdatable.UpdateReleaseTagPattern);
    const editReleaseTagPatternSuccess = useUpdated(AppSettingsUpdatable.UpdateReleaseTagPattern);

    React.useEffect(() => {
        if (editReleaseTagPatternSuccess) {
            globalMessage.success('Release tag pattern updated!');
            onChange(false);
        }
    }, [editReleaseTagPatternSuccess]);

    React.useEffect(() => {
        if (editReleaseTagPatternError) {
            globalMessage.error(editReleaseTagPatternError);
        }
    }, [editReleaseTagPatternError, addReleaseTagPatternError]);

    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));

    React.useEffect(() => {
        if (!releaseTag && !releaseTagGuess) {
            const request: AppEntityRequest = {
                orgId: orgId,
                appId: appId,
            };

            setReleaseTagGuessIsLoading(true);

            API.getSuggestedReleaseTagPattern(request, authCredentials)
                .then((response) => {
                    setReleaseTagGuess(response);
                })
                .catch(() => {
                    return;
                })
                .finally(() => {
                    setReleaseTagGuessIsLoading(false);
                });
        }
    }, [releaseTagGuess]);

    const onApplySuggestionButton = () => {
        form.setFieldsValue({ releaseTagPattern: releaseTagGuess });
        onChange(checkIfHasUnsavedChanges());
    };

    const editReleaseTagPattern = (values) => {
        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.UpdateReleaseTagPattern, {
                orgId,
                appId,
                pattern: values.releaseTagPattern,
                tagPatternId: releaseTag ? releaseTag.id : null,
            }),
        );
    };

    const initialValues = {
        releaseTagPattern: releaseTag ? releaseTag.pattern : null,
    };

    const checkIfHasUnsavedChanges = () => {
        const formValues = form.getFieldsValue();
        return !isEqual(formValues, initialValues);
    };
    const availableTagTokens = AllPatternTokens.filter((token) => !DisallowedTokensForTag.includes(token));

    return (
        <Form
            form={form}
            onFinish={releaseTag ? editReleaseTagPattern : addReleaseTagPattern}
            initialValues={initialValues}
            onFieldsChange={() => onChange(checkIfHasUnsavedChanges())}
        >
            <Space direction="vertical" style={{ width: '100%' }} size={20}>
                <Typography.Text
                    style={{ color: antGrey.grey7, fontSize: 14, fontWeight: 400, maxWidth: 460, display: 'block' }}
                >
                    Do you tag all of your builds? That’s fine — you can keep doing that, but Runway will also need a
                    unique, final tag to mark each release.
                </Typography.Text>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Form.Item style={{ flex: 1, marginBottom: 0 }}>
                        <Form.Item
                            name="releaseTagPattern"
                            style={{ marginBottom: 0 }}
                            rules={[
                                {
                                    validator: (rule, value) => {
                                        const pattern = value as string;

                                        return validatePattern(pattern, 'Tag', RequireAtLeastOneOfTokensForTag, []);
                                    },
                                },
                            ]}
                            extra={
                                <Space direction="vertical" size={2} style={{ marginTop: 8 }}>
                                    <SupportedTokensComponent
                                        availableTokens={availableTagTokens}
                                        prefixText="Pattern strings with"
                                    />
                                    <Text type="secondary">
                                        Read more in our{' '}
                                        <Typography.Link
                                            href="https://docs.runway.team/getting-started/pattern-strings-tokens"
                                            target="_blank"
                                        >
                                            docs.
                                        </Typography.Link>
                                    </Text>
                                </Space>
                            }
                        >
                            <Input style={{ maxWidth: 400 }} placeholder="e.g. v{version}" autoComplete="off" />
                        </Form.Item>
                        {releaseTagGuessIsLoading && (
                            <p>
                                <Spin size="small" />
                            </p>
                        )}
                        {releaseTagGuess && (
                            <Alert
                                style={{
                                    maxWidth: 600,
                                    marginTop: '1em',
                                    marginBottom: '1em',
                                    backgroundColor: `${runwayColors.primary}10`,
                                    border: `1px solid ${runwayColors.primary}50`,
                                }}
                                message={
                                    <>
                                        Suggested:{' '}
                                        <Text code style={{ margin: '1em' }}>
                                            {releaseTagGuess}
                                        </Text>
                                        <Tooltip title="Suggestion for this field based on the tags we found in your repository">
                                            <InfoCircleOutlined
                                                style={{
                                                    color: runwayColors.info,
                                                }}
                                            />
                                        </Tooltip>
                                    </>
                                }
                                action={
                                    <Button size="small" onClick={onApplySuggestionButton}>
                                        Apply
                                    </Button>
                                }
                            />
                        )}
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }} shouldUpdate>
                        {() => (
                            <Button
                                loading={editReleaseTagPatternIsLoading || addReleaseTagPatternIsLoading}
                                disabled={!checkIfHasUnsavedChanges()}
                                type="primary"
                                htmlType="submit"
                                icon={<ArrowRightOutlined />}
                            >
                                Save
                            </Button>
                        )}
                    </Form.Item>
                </div>
            </Space>
        </Form>
    );
};

const SetupViewComponent: React.FC<SetupViewProps> = (props) => {
    const releaseTag = useSelector((state: RootState) => selectAppSettings(state, props.appId).releaseTagPatterns?.[0]);

    const [activeKey, setActiveKey] = React.useState('1');

    const taggedCommit = useSelector((state: RootState) => selectAppSettings(state, props.appId).latestReleaseTag);

    const vcsIntegration = useSelector((state: RootState) => selectVCSIntegration(state, props.appId));

    const [integrationIcon, integrationIconAlt] = vcsIntegration ? iconAndAltForIntegrationType(vcsIntegration.id) : [];

    React.useEffect(() => {
        if (releaseTag) {
            setActiveKey('2');
        }
    }, [releaseTag]);

    return (
        <Collapse
            defaultActiveKey={[activeKey]}
            activeKey={[activeKey]}
            bordered={false}
            expandIcon={() => null}
            accordion
            className="setup-steps-accordion"
            onChange={(activeKey) => {
                setActiveKey(activeKey as string);
            }}
        >
            <Collapse.Panel
                key="1"
                extra={
                    activeKey !== '1' &&
                    releaseTag && (
                        <>
                            <Text code>{releaseTag.pattern}</Text>
                            <CheckOutlined style={{ marginLeft: 8, color: runwayColors.statusGreen }} />
                        </>
                    )
                }
                header={
                    <>
                        <Typography.Text
                            style={{ color: antGrey.grey8, fontSize: 16, fontWeight: activeKey === '1' ? 400 : 600 }}
                        >
                            {activeKey === '1' ? 'What format will release tags be in?' : 'Release tag pattern'}
                        </Typography.Text>
                    </>
                }
            >
                <ReleaseTagPatternModule {...props} releaseTag={releaseTag} />
            </Collapse.Panel>
            {releaseTag && (
                <Collapse.Panel
                    extra={
                        taggedCommit && (
                            <>
                                <CheckOutlined style={{ marginLeft: 8, color: runwayColors.statusGreen }} />
                            </>
                        )
                    }
                    header={
                        taggedCommit ?
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0,
                                }}
                            >
                                <Typography.Text
                                    style={{ color: antGrey.grey7, fontSize: 14, fontWeight: 400, marginRight: 6 }}
                                >
                                    {vcsIntegration && (
                                        <img
                                            src={integrationIcon}
                                            alt={integrationIconAlt}
                                            width={25}
                                            height={25}
                                            style={{ marginBottom: 3 }}
                                        />
                                    )}{' '}
                                    Last release tag detected
                                </Typography.Text>
                                <Typography.Text code>{taggedCommit.name}</Typography.Text>
                                <Button type="link" href={taggedCommit.url} target="_blank" icon={<LinkOutlined />} />
                                {'-'}
                                <Button
                                    type="link"
                                    href={taggedCommit.commitURL}
                                    target="_blank"
                                    style={{ padding: '0 6px' }}
                                >
                                    ({taggedCommit.commitHash.substring(0, 7)})
                                </Button>
                                <Typography.Text
                                    style={{
                                        width: 300,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {taggedCommit.commitMessage}
                                </Typography.Text>
                            </div>
                        :   <>
                                <Typography.Text
                                    style={{
                                        color: antGrey.grey7,
                                        fontSize: 16,
                                        fontWeight: 400,
                                    }}
                                >
                                    <span
                                        style={{
                                            color: antGrey.grey8,
                                            fontWeight: activeKey === '2' ? 400 : 600,
                                        }}
                                    >
                                        Choose a commit to tag for your last release.
                                    </span>
                                </Typography.Text>
                            </>
                    }
                    key="2"
                >
                    <OptionalReleaseTagModule {...props} releaseTag={releaseTag} taggedCommit={taggedCommit} />
                </Collapse.Panel>
            )}
        </Collapse>
    );
};

const InfoViewComponent: React.FC<AppComponentProps> = () => {
    return (
        <Space direction="vertical">
            <InfoTip>
                Tags are important markers that help delineate code release-to-release. Runway relies on tags to
                understand which parts of the code are new in a given release version.
            </InfoTip>
            <AutomationTip>
                <span>
                    Once you’re set up, we’ll do this for you automatically! Runway’s automation will handle release
                    tagging for you, forever. Read more about tokenized patterns in our{' '}
                    <Typography.Link
                        href="https://docs.runway.team/getting-started/pattern-strings-tokens"
                        target="_blank"
                    >
                        docs.
                    </Typography.Link>
                </span>
            </AutomationTip>
            <HelpTip>
                Questions, or want a walkthrough?{' '}
                <Button type="link" style={{ padding: 0, display: 'inline', marginLeft: 5 }} href={`#hs-chat-open`}>
                    Let us know!
                </Button>
            </HelpTip>
        </Space>
    );
};

export const ReleaseTagsStep: SetupStep = {
    title: 'Let’s talk about your release tags.',
    description: () => {
        return 'Aside from being best practice for release hygiene, Runway uses your release tags to differentiate the code work going into each release.';
    },
    icon: () => TagOutlined,
    SetupViewComponent,
    InfoViewComponent,
    listTitle: 'Release tags',
    listDescription: 'Let’s talk about release tags.',
    integrationsRecommendationBannerData: {
        title: () => {
            return 'Connect your version control system';
        },
        description:
            'Runway can automate code tasks like branching and tagging, and intelligently reconcile code and project management tickets to give you a true picture of feature completeness.',
        actionText: 'OK, let’s do it',
        type: SetupStepType.ReleaseTags,
        integrationCapabilities: [IntegrationCapability.VersionControl],
    },
    isCompleteSelector: (root, { appId }) => {
        const releaseTagPattern = selectAppSettings(root, appId).releaseTagPatterns?.[0]?.pattern;
        const hasVCSConnected = selectHasVCSIntegrationConnected(root, appId);
        const latestReleaseTag = selectLastCompletedVersionTag(root, appId);
        const latestVersion = selectLatestVersion(root, appId);
        const isUserAdmin = selectUserIsAdmin(root);

        const requiredTagPresent = hasVCSConnected && latestVersion ? !!latestReleaseTag : true;

        const hasAtLeastNReleasesTagged = selectHasAtLeastNReleasesTagged(root, appId, 2);

        const wasAppCreatedAfterQualitativeOnboarding = selectWasAppCreatedAfterQualitativeOnboarding(root, appId);

        const wasVisited = getSetupStepLocalData(appId, SetupStepType.ReleaseTags)?.hasBeenVisited;
        const requiredDataPresent = !!releaseTagPattern && (requiredTagPresent || hasAtLeastNReleasesTagged);

        return wasAppCreatedAfterQualitativeOnboarding && isUserAdmin ?
                requiredDataPresent && wasVisited
            :   requiredDataPresent;
    },
};
